import React, { useEffect, useState, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import { Link, useParams } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { useDispatch } from "react-redux";
import { addCart } from "../redux/action";
import toast from "react-hot-toast";

import { Footer, Navbar, AnnouncementBar } from "../components";
import { formatPKRFromUSD, formatDiscountedPKRFromUSD } from "../utils/currency";
import { getProductById, getProducts } from "../utils/api";
import { getReviews, addReview } from "../utils/reviews";

const Product = () => {
  const { id } = useParams();
  const [product, setProduct] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [revName, setRevName] = useState("");
  const [revRating, setRevRating] = useState(5);
  const [revComment, setRevComment] = useState("");
  const reviewFormRef = useRef(null);
  const isSubmittingRef = useRef(false);

  const dispatch = useDispatch();

  const addProduct = (product) => {
    dispatch(addCart(product));
  };

  useEffect(() => {
    const getProduct = async () => {
      setLoading(true);
      setLoading2(true);
      const data = await getProductById(id);
      setProduct(data);
      setLoading(false);
      const data2 = await getProducts(data.category);
      setSimilarProducts(data2);
      setLoading2(false);
      setReviews(getReviews(id));
    };
    getProduct();
  }, [id]);

  const Loading = () => {
    return (
      <>
        <div className="container my-5 py-2">
          <div className="row">
            <div className="col-md-6 py-3">
              <Skeleton height={400} width={400} />
            </div>
            <div className="col-md-6 py-5">
              <Skeleton height={30} width={250} />
              <Skeleton height={90} />
              <Skeleton height={40} width={70} />
              <Skeleton height={50} width={110} />
              <Skeleton height={120} />
              <Skeleton height={40} width={110} inline={true} />
              <Skeleton className="mx-3" height={40} width={110} />
            </div>
          </div>
        </div>
      </>
    );
  };

  const ShowProduct = () => {
    return (
      <>
        <div className="container my-5 py-2">
          <div className="row">
            <div className="col-md-6 col-sm-12 py-3">
              <img
                className="img-fluid"
                src={product.image}
                alt={product.title}
                width="400px"
                height="400px"
              />
            </div>
            <div className="col-md-6 col-md-6 py-5">
              <h4 className="text-uppercase text-muted">{product.category}</h4>
              <h1 className="display-5">{product.title}</h1>
              {product.isNew && (
                <span className="badge bg-warning text-dark ms-2">{product.newLabel || 'New'}</span>
              )}
              <p className="lead">
                {product.rating && product.rating.rate}{" "}
                <i className="fa fa-star"></i>
              </p>
              <div className="my-3">
                {!product.hidePrice && (
                  <div className="d-flex align-items-baseline gap-3">
                    <span className="text-muted text-decoration-line-through">{formatPKRFromUSD(product.price)}</span>
                    <h3 className="display-6 my-0">
                      {product.discountRate===0.5
                        ? formatPKRFromUSD(product.price * 0.5)
                        : formatDiscountedPKRFromUSD(product.price)}
                    </h3>
                    <span className="badge bg-danger badge-glow">{product.discountRate===0.5 ? '50% OFF' : '30% OFF'}</span>
                  </div>
                )}
                <div className="mt-2 d-flex gap-2">
                  <span className="badge bg-success badge-glow">Free Delivery Across Pakistan</span>
                  {product.dealType && (
                    <span className="badge bg-warning text-dark badge-glow">{product.dealType}</span>
                  )}
                </div>
              </div>
              <p className="lead">{product.description}</p>
              <button
                className="btn btn-outline-dark"
                onClick={() => addProduct(product)}
              >
                Add to Cart
              </button>
              <Link to="/cart" className="btn btn-dark mx-3">
                Go to Cart
              </Link>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="container my-4">
          <h3 className="mb-3">Customer Reviews</h3>
          {reviews.length === 0 ? (
            <p className="text-muted">No reviews yet. Be the first to review this product.</p>
          ) : (
            <div className="list-group mb-4">
              {reviews.map(r => (
                <div key={r.id} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-center">
                    <strong>{r.name}</strong>
                    <small className="text-muted">{new Date(r.date).toLocaleDateString()}</small>
                  </div>
                  <div className="mb-1" aria-label={`Rating ${r.rating} out of 5`}>
                    {[1,2,3,4,5].map(i => (
                      <i key={i} className={`fa ${i <= r.rating ? 'fa-star text-warning' : 'fa-star text-muted'}`}></i>
                    ))}
                    {r.verified && <span className="badge bg-success ms-2">Verified</span>}
                  </div>
                  <div>{r.comment}</div>
                </div>
              ))}
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Write a review</h5>
              <form 
                ref={reviewFormRef}
                onSubmit={(e)=>{
                  e.preventDefault();
                  e.stopPropagation();
                  
                  if (isSubmittingRef.current) return; // Prevent double submission
                  isSubmittingRef.current = true;
                  
                  if (!revName.trim() || revName.trim().length < 2) { 
                    toast.error('Please enter your name'); 
                    isSubmittingRef.current = false;
                    return; 
                  }
                  if (!revComment.trim() || revComment.trim().length < 10) { 
                    toast.error('Please write at least 10 characters'); 
                    isSubmittingRef.current = false;
                    return; 
                  }
                  
                  // Prevent any scrolling during form submission
                  const currentScrollPosition = window.pageYOffset;
                  
                  addReview(id, { name: revName.trim(), rating: Number(revRating), comment: revComment.trim() });
                  setReviews(getReviews(id));
                  setRevName(''); 
                  setRevRating(5); 
                  setRevComment('');
                  
                  // Restore scroll position after state updates
                  setTimeout(() => {
                    window.scrollTo(0, currentScrollPosition);
                    isSubmittingRef.current = false;
                  }, 100);
                  
                  toast.success('Thanks for your review!');
                }}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Name</label>
                    <input 
                      className="form-control" 
                      value={revName} 
                      onChange={(e) => {
                        e.preventDefault();
                        setRevName(e.target.value);
                      }}
                      onFocus={(e) => e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' })}
                      placeholder="Your name" 
                      required 
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Rating</label>
                    <select 
                      className="form-select" 
                      value={revRating} 
                      onChange={(e) => {
                        e.preventDefault();
                        setRevRating(e.target.value);
                      }}
                    >
                      {[5,4,3,2,1].map(v => <option key={v} value={v}>{v} Star{v>1?'s':''}</option>)}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Comment</label>
                    <textarea 
                      className="form-control" 
                      rows="3" 
                      value={revComment} 
                      onChange={(e) => {
                        e.preventDefault();
                        setRevComment(e.target.value);
                      }}
                      onFocus={(e) => e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' })}
                      placeholder="Share your experience..." 
                      required 
                    />
                  </div>
                  <div className="col-12">
                    <button type="submit" className="btn btn-dark">Submit Review</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </>
    );
  };

  const Loading2 = () => {
    return (
      <>
        <div className="my-4 py-4">
          <div className="d-flex">
            <div className="mx-4">
              <Skeleton height={400} width={250} />
            </div>
            <div className="mx-4">
              <Skeleton height={400} width={250} />
            </div>
            <div className="mx-4">
              <Skeleton height={400} width={250} />
            </div>
            <div className="mx-4">
              <Skeleton height={400} width={250} />
            </div>
          </div>
        </div>
      </>
    );
  };

  const ShowSimilarProduct = () => {
    return (
      <>
        <div className="py-4 my-4">
          <div className="d-flex">
            {similarProducts.map((item) => {
              return (
                <div key={item.id} className="card mx-4 text-center">
                  <img
                    className="card-img-top p-3"
                    src={item.image}
                    alt="Card"
                    height={300}
                    width={300}
                  />
                  <div className="card-body">
                    <h5 className="card-title">
                      {item.title}
                    </h5>
                  </div>
                  {/* <ul className="list-group list-group-flush">
                    <li className="list-group-item lead">${product.price}</li>
                  </ul> */}
                  <div className="card-body">
                    <Link
                      to={"/product/" + item.id}
                      className="btn btn-dark m-1"
                    >
                      Buy Now
                    </Link>
                    <button
                      className="btn btn-dark m-1"
                      onClick={() => addProduct(item)}
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  };
  return (
    <>
  <AnnouncementBar />
  <Navbar />
      <div className="container">
        <div className="row">{loading ? <Loading /> : <ShowProduct />}</div>
        <div className="row my-5 py-5">
          <div className="d-none d-md-block">
          <h2 className="">You may also Like</h2>
            <Marquee
              pauseOnHover={true}
              pauseOnClick={true}
              speed={50}
            >
              {loading2 ? <Loading2 /> : <ShowSimilarProduct />}
            </Marquee>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Product;
